/**
 * Tiny built-in i18n for the RAGSense public chat widget.
 *
 * Goals:
 *   - zero runtime dependencies (widget already bundles marked; we don't
 *     want to pull i18next into a third-party embed)
 *   - supports six languages out of the box: en, fr, es, de, cs, sk
 *   - falls back to English for unknown keys or unsupported languages
 *   - overridable from the widget config (server-driven) or from `options.language`
 */

const STRINGS = {
  en: {
    openChat: 'Open chat',
    closeChat: 'Close chat',
    chatTitle: 'Chat with us',
    newChat: 'New chat',
    newChatAria: 'Start a new chat',
    expand: 'Expand',
    expandAria: 'Expand chat',
    collapse: 'Collapse',
    collapseAria: 'Collapse chat',
    messagePlaceholder: 'Type your message\u2026',
    messageAria: 'Message input',
    send: 'Send',
    legal: 'Privacy notice',
    disclaimer: 'Your chat messages and technical data may be used to operate, secure, and improve this service.',
    connecting: 'Connecting to chat\u2026',
    initError: 'Couldn\u2019t start the chat. Please try again later.',
    retry: 'Try again',
    sendError: 'Couldn\u2019t send your message. Please try again.',
    itemFallback: 'Item',
    thinking: 'Thinking\u2026',
    resources: 'Resources'
  },
  fr: {
    openChat: 'Ouvrir le chat',
    closeChat: 'Fermer le chat',
    chatTitle: 'Discuter avec nous',
    newChat: 'Nouveau chat',
    newChatAria: 'Commencer un nouveau chat',
    expand: 'Agrandir',
    expandAria: 'Agrandir le chat',
    collapse: 'Réduire',
    collapseAria: 'Réduire le chat',
    messagePlaceholder: 'Tapez votre message\u2026',
    messageAria: 'Champ de message',
    send: 'Envoyer',
    legal: 'Avis de confidentialité',
    disclaimer: 'Vos messages et données techniques peuvent être utilisés pour faire fonctionner, sécuriser et améliorer ce service.',
    connecting: 'Connexion au chat\u2026',
    initError: 'Impossible de démarrer le chat. Merci de réessayer plus tard.',
    retry: 'Réessayer',
    sendError: 'Impossible d\u2019envoyer votre message. Réessayez.',
    itemFallback: 'Élément',
    thinking: 'Réflexion\u2026',
    resources: 'Ressources'
  },
  es: {
    openChat: 'Abrir chat',
    closeChat: 'Cerrar chat',
    chatTitle: 'Habla con nosotros',
    newChat: 'Nuevo chat',
    newChatAria: 'Empezar un chat nuevo',
    expand: 'Ampliar',
    expandAria: 'Ampliar el chat',
    collapse: 'Reducir',
    collapseAria: 'Reducir el chat',
    messagePlaceholder: 'Escribe tu mensaje\u2026',
    messageAria: 'Campo de mensaje',
    send: 'Enviar',
    legal: 'Aviso de privacidad',
    disclaimer: 'Tus mensajes y datos técnicos pueden usarse para operar, proteger y mejorar este servicio.',
    connecting: 'Conectando con el chat\u2026',
    initError: 'No se pudo iniciar el chat. Inténtalo más tarde.',
    retry: 'Reintentar',
    sendError: 'No se pudo enviar tu mensaje. Inténtalo de nuevo.',
    itemFallback: 'Elemento',
    thinking: 'Pensando\u2026',
    resources: 'Recursos'
  },
  de: {
    openChat: 'Chat öffnen',
    closeChat: 'Chat schließen',
    chatTitle: 'Schreib uns',
    newChat: 'Neuer Chat',
    newChatAria: 'Einen neuen Chat starten',
    expand: 'Vergrößern',
    expandAria: 'Chat vergrößern',
    collapse: 'Verkleinern',
    collapseAria: 'Chat verkleinern',
    messagePlaceholder: 'Nachricht eingeben\u2026',
    messageAria: 'Nachrichteneingabe',
    send: 'Senden',
    legal: 'Datenschutzhinweis',
    disclaimer: 'Ihre Nachrichten und technische Daten können verwendet werden, um diesen Dienst zu betreiben, zu sichern und zu verbessern.',
    connecting: 'Chat wird verbunden\u2026',
    initError: 'Der Chat konnte nicht gestartet werden. Bitte versuchen Sie es später noch einmal.',
    retry: 'Erneut versuchen',
    sendError: 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.',
    itemFallback: 'Eintrag',
    thinking: 'Überlege\u2026',
    resources: 'Ressourcen'
  },
  cs: {
    openChat: 'Otevřít chat',
    closeChat: 'Zavřít chat',
    chatTitle: 'Napište nám',
    newChat: 'Nový chat',
    newChatAria: 'Začít nový chat',
    expand: 'Zvětšit',
    expandAria: 'Zvětšit chat',
    collapse: 'Zmenšit',
    collapseAria: 'Zmenšit chat',
    messagePlaceholder: 'Napište zprávu\u2026',
    messageAria: 'Pole pro zprávu',
    send: 'Odeslat',
    legal: 'Zásady ochrany osobních údajů',
    disclaimer: 'Vaše zprávy a technické údaje lze použít k provozu, zabezpečení a zlepšení této služby.',
    connecting: 'Připojování k chatu\u2026',
    initError: 'Chat se nepodařilo spustit. Zkuste to prosím později.',
    retry: 'Zkusit znovu',
    sendError: 'Zprávu se nepodařilo odeslat. Zkuste to znovu.',
    itemFallback: 'Položka',
    thinking: 'Přemýšlím\u2026',
    resources: 'Zdroje'
  },
  sk: {
    openChat: 'Otvoriť chat',
    closeChat: 'Zavrieť chat',
    chatTitle: 'Napíšte nám',
    newChat: 'Nový chat',
    newChatAria: 'Začať nový chat',
    expand: 'Zväčšiť',
    expandAria: 'Zväčšiť chat',
    collapse: 'Zmenšiť',
    collapseAria: 'Zmenšiť chat',
    messagePlaceholder: 'Napíšte správu\u2026',
    messageAria: 'Pole pre správu',
    send: 'Odoslať',
    legal: 'Zásady ochrany osobných údajov',
    disclaimer: 'Vaše správy a technické údaje sa môžu použiť na prevádzku, zabezpečenie a zlepšenie tejto služby.',
    connecting: 'Pripájanie k chatu\u2026',
    initError: 'Chat sa nepodarilo spustiť. Skúste to neskôr.',
    retry: 'Skúsiť znova',
    sendError: 'Správu sa nepodarilo odoslať. Skúste to znova.',
    itemFallback: 'Položka',
    thinking: 'Premýšľam\u2026',
    resources: 'Zdroje'
  }
};

export const WIDGET_SUPPORTED_LANGUAGES = Object.keys(STRINGS);

/**
 * Pick a supported 2-letter language from whatever the caller gives us.
 * Accepts: 'fr', 'fr-FR', 'FR_fr', 'zh' (falls back to 'en'), undefined, etc.
 */
export function resolveLanguage(raw) {
  if (!raw) return 'en';
  const short = String(raw).slice(0, 2).toLowerCase();
  return STRINGS[short] ? short : 'en';
}

export function detectLanguage(configOrOptions = {}) {
  const explicit = configOrOptions.language
    || configOrOptions.default_language
    || configOrOptions.preferred_language;
  if (explicit) return resolveLanguage(explicit);

  if (typeof navigator !== 'undefined' && navigator.language) {
    return resolveLanguage(navigator.language);
  }
  return 'en';
}

/**
 * Build a translator bound to a specific language.
 */
export function makeTranslator(lang) {
  const resolved = resolveLanguage(lang);
  const bundle = STRINGS[resolved];
  const fallback = STRINGS.en;

  return function t(key) {
    if (bundle && Object.prototype.hasOwnProperty.call(bundle, key)) {
      return bundle[key];
    }
    return fallback[key] ?? key;
  };
}

export const widgetI18n = {
  supported: WIDGET_SUPPORTED_LANGUAGES,
  resolve: resolveLanguage,
  detect: detectLanguage,
  translator: makeTranslator,
};

export default widgetI18n;
